import { useEffect, useRef, useState } from 'react';
import { ButtonIcon, Tooltip, TooltipTrigger } from '@backstage/ui';
import { waadooIcons } from './icons/waadooIcons';

/** How long « Copié » stays after a copy. */
const COPIED_MS = 2000;

/** The words of the button: its name, then what a copy did. */
export interface CopyCommandLabels {
  copy: string;
  copied: string;
  failed: string;
}

/**
 * The French words of the project deletion page (`DeletionReport.tsx`),
 * whose strings are written in the component; a translated screen passes
 * its own.
 */
const FRENCH_LABELS: CopyCommandLabels = {
  copy: 'Copier la commande',
  copied: 'Copié',
  failed: 'Copie impossible : sélectionnez la commande à la main',
};

/**
 * The copy button of a command block, at its top right (`.waadoo-copy-command`
 * in styles.css, the block positioned): the command to the clipboard, then a
 * check for {@link COPIED_MS} — said by a live region too. The clipboard is
 * unavailable outside a secure context and may be refused: said rather than
 * swallowed, as the credentials card does.
 *
 * Shared by the project deletion's report and the ephemeral branch's
 * « Compte rendu » (projectEnvironments/BranchReady.tsx).
 */
export function CopyCommandButton(props: {
  text: string;
  labels?: CopyCommandLabels;
}) {
  const labels = props.labels ?? FRENCH_LABELS;
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const timer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(props.text);
      setState('copied');
    } catch {
      setState('failed');
    }
    // Cleared before arming: a second click restarts the delay.
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setState('idle'), COPIED_MS);
  };

  const CopyIcon = waadooIcons.copy;
  const CopiedIcon = waadooIcons.stepDone;
  const FailedIcon = waadooIcons.statusFailed;
  let icon = <CopyIcon aria-hidden focusable="false" />;
  let message = '';
  if (state === 'copied') {
    icon = <CopiedIcon aria-hidden focusable="false" />;
    message = labels.copied;
  } else if (state === 'failed') {
    icon = <FailedIcon aria-hidden focusable="false" />;
    message = labels.failed;
  }
  return (
    <>
      <TooltipTrigger>
        <ButtonIcon
          className="waadoo-copy-command"
          variant="secondary"
          size="small"
          aria-label={labels.copy}
          icon={icon}
          onPress={() => void copy()}
        />
        <Tooltip>{message || labels.copy}</Tooltip>
      </TooltipTrigger>
      <span className="waadoo-visually-hidden" role="status">
        {message}
      </span>
    </>
  );
}
